<template>
    <div>
        <rkm-table
            :fields="fields"
            :items="items"
            hide-filters
            hide-pagination
        >
            <template v-slot:cell(info)="{index, item}">
                <div v-if="item.centroCusto">
                    <rkm-icon
                        :id="`sinal-vital-info-${index}`"
                        class="mr-2"
                        icon="info-circle"
                    />

                    <rkm-popover
                        :target="`sinal-vital-info-${index}`"
                        triggers="hover"
                    >
                        <div>{{item.centroCusto.setor.nome}}</div>
                        <div><rkm-icon icon="user" /> {{item.lotacao.profissional.nome}} - {{item.lotacao.ocupacao.nome}}</div>
                        <div><rkm-icon :icon="['far', 'clock']" /> {{item.dataHora}}</div>
                    </rkm-popover>

                    {{item.dataHora}}
                </div>
            </template>

            <template v-slot:cell(idadeGestacional)="{item}">
                {{item.idadeGestacional.semanas}}s {{item.idadeGestacional.dias}}s
            </template>

            <template v-slot:cell(movimentacaoFetal)="{item}">
                <template v-if="item.movimentacaoFetal !== null">
                    {{item.movimentacaoFetal ? 'Sim' : 'Não'}}
                </template>
            </template>
        </rkm-table>

        <rkm-row class="mt-2">
            <rkm-col cols="12">
                <rkm-chart-scatter
                    :chart-data="chartData"
                    :chart-options="chartOptions"
                />
            </rkm-col>
        </rkm-row>
    </div>
</template>

<script>
// Mixins.
import {object} from '@/mixins'

// Others.
import moment from 'moment'

export default {
    computed: {
        items() {
            return this.data.form.medicoesGestacao
        },
        chartParams() {
            return this.data.form.gestacao.grafico
        },
        chartOptions() {
            return this.getChartOptions(this.getIndexesToHideLabel())
        },
        chartData() {
            return {
                datasets: this.getDatasets()
            }
        },
    },
    data: () => ({
        fields: [
            {class: 'col-min', key: 'ordemAtendimento', label: 'Consulta'},
            {class: 'col-min', key: 'info', label: 'Data / Hora'},
            {key: 'idadeGestacional', label: 'Idade gestacional', sortable: true},
            {key: 'edemaNome', label: 'Edema', sortable: true},
            {key: 'alturaUterina', label: 'Altura uterina (cm)', sortable: true},
            {key: 'batimentoCardiacoFetal', label: 'Batimento cardíaco fetal (bpm)', sortable: true},
            {key: 'movimentacaoFetal', label: 'Movimentação fetal', sortable: true},
        ],
        others: {moment},
    }),
    methods: {
        getDatasets() {
            const maxYScale = this.getMaxYScale()

            const datasets = this.chartParams.measures.map(measure => {
                let color = null
                let fill = null
                let identifier = null
                let label = null
                let showLine = true

                if (measure.measure === 'p10') {
                    color = '#FF0000'
                    fill = 'start'
                    identifier = 'p10',
                    label = 'Abaixo do esperado'
                }
                else if (measure.measure === 'p90') {
                    color = '#00FF00'
                    fill = '-1'
                    identifier = 'p90',
                    label = 'Dentro do esperado'
                }
                else if (measure.measure === 'max') {
                    color = '#FF0000'
                    fill = '-1'
                    identifier = 'max',
                    label = 'Acima do esperado'
                    showLine = false

                    measure.axis = measure.axis.map(axis => {
                        return {
                            x: axis.x,
                            y: maxYScale,
                        }
                    })
                }

                return {
                    backgroundColor: `${color}22`,
                    borderColor: color,
                    borderDash: [5,5],
                    borderWidth: 1,
                    data: measure.axis,
                    fill,
                    identifier,
                    label: label,
                    pointHitRadius: 0,
                    pointRadius: 0,
                    showLine,
                }
            })

            datasets.push({
                backgroundColor: "#000000",
                borderColor: "#000000",
                borderWidth: 1,
                data: this.items.filter(item => item.idadeGestacional.semanas >= 13).map(item => {
                    return {
                        x: item.idadeGestacional.semanas,
                        y: item.alturaUterina,
                    }
                }),
                fill: false,
                identifier: null,
                label: 'Medição',
                showLine: true,
            })

            return datasets
        },
        getChartOptions(indexesToHideLabel = []) {
            const defaultChartOptions = {
                animation: false,
                aspectRatio: 2,
                maintainAspectRatio: false,
                plugins: {
                    decimation: {enabled: true},
                    legend: {
                        labels: {
                            filter: function(item) {
                                return !indexesToHideLabel.includes(item.datasetIndex)
                            },
                        },
                        onClick: null,
                    },
                },
                spanGaps: true,
            }

            return {
                ...defaultChartOptions,
                ...this.getScales(),
            }
        },
        getIndexesToHideLabel() {
            const indexes = []

            this.chartData.datasets.forEach((dataset, index) => {

                if ([null].includes(dataset.identifier)) {
                    indexes.push(index)
                }
            })

            return indexes
        },
        getScales() {
            const y = {
                min: this.getMinYScale(),
                max: this.getMaxYScale(),
                ticks: {
                    autoSkip: false,
                    stepSize: this.chartParams.scales.y.stepSize,
                },
                title: {
                    display: true,
                    font: {weight: 'bold'},
                    text: this.chartParams.scales.y.title,
                },
            }

            return {
                scales: {
                    x: {
                        min: this.chartParams.scales.x.min,
                        max: this.chartParams.scales.x.max,
                        ticks: {
                            autoSkip: false,
                            stepSize: this.chartParams.scales.x.stepSize,
                        },
                        title: {
                            display: true,
                            font: {weight: 'bold'},
                            text: this.chartParams.scales.x.title,
                        },
                    },
                    y: {
                        ...y,
                        position: 'left',
                    },
                    y2: {
                        ...y,
                        position: 'right',
                    },
                },
            }
        },
        getMinYScale() {
            let minYScale = Math.min(...this.items.map(item => item.alturaUterina)) + this.chartParams.scales.y.stepSize

            if (minYScale < 0) {
                minYScale = 0
            }

            return minYScale < this.chartParams.scales.y.min ? minYScale : this.chartParams.scales.y.min
        },
        getMaxYScale() {
            let maxYScale = Math.max(...this.items.map(item => item.alturaUterina)) + this.chartParams.scales.y.stepSize

            return maxYScale > this.chartParams.scales.y.max ? maxYScale : this.chartParams.scales.y.max
        },
    },
    mixins: [object],
    props: {
        data: {required: true, type: Object},
    },
}
</script>